import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import StorageIcon from '@mui/icons-material/Storage';
import PeopleIcon from '@mui/icons-material/People';
import FolderIcon from '@mui/icons-material/Folder';
import BarChartIcon from '@mui/icons-material/BarChart';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import api from '../../../services/api';

const AuditLog = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({
    activeProjects: 0,
    activeUsers: 0,
    storageUsage: {
      total: 0,
      used: 0
    },
    activityByDay: []
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalLogs, setTotalLogs] = useState(0);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
  const [detailDialog, setDetailDialog] = useState({ open: false, details: null, log: null });
  
  // Filter states
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    userId: '',
    action: '',
    entityType: '',
    entityId: '',
    projectId: ''
  });
  
  // Available actions for filtering
  const logActions = [
    { value: '', label: 'Semua Aksi' },
    { value: 'LOGIN', label: 'Login' },
    { value: 'LOGOUT', label: 'Logout' },
    { value: 'CREATE', label: 'Create' },
    { value: 'UPDATE', label: 'Update' },
    { value: 'DELETE', label: 'Delete' },
    { value: 'UPLOAD', label: 'Upload' },
    { value: 'DOWNLOAD', label: 'Download' },
    { value: 'APPROVE', label: 'Approve' },
    { value: 'REJECT', label: 'Reject' },
    { value: 'SETTINGS', label: 'Settings' },
    { value: 'COMMENT', label: 'Comment' },
    { value: 'ASSIGN', label: 'Assign' },
    { value: 'VIEW', label: 'View' }
  ];
  
  // Available entity types for filtering
  const entityTypes = [
    { value: '', label: 'Semua Entitas' },
    { value: 'User', label: 'User' },
    { value: 'Project', label: 'Project' },
    { value: 'Task', label: 'Task' },
    { value: 'Document', label: 'Document' },
    { value: 'Progress', label: 'Progress' },
    { value: 'Settings', label: 'Settings' },
    { value: 'System', label: 'System' }
  ];

  // Menggunakan useCallback untuk memastikan fungsi fetchLogs stabil antar render
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage
      };
      
      // Add filters if they exist
      if (filters.startDate && filters.endDate) {
        params.startDate = filters.startDate.toISOString();
        params.endDate = filters.endDate.toISOString();
      }
      if (filters.userId) {
        params.userId = filters.userId;
      }
      if (filters.action) {
        params.action = filters.action;
      }
      if (filters.entityType) {
        params.entityType = filters.entityType;
      }
      if (filters.entityId) {
        params.entityId = filters.entityId;
      }
      if (filters.projectId) {
        params.projectId = filters.projectId;
      }
      
      const response = await api.get('/admin/logs', { params });
      
      setLogs(response.data.logs || []);
      setTotalLogs(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching logs:', error);
      setAlert({
        open: true,
        message: 'Gagal memuat log aktivitas',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);
  
  // Menggunakan useCallback untuk fungsi fetchStats
  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data.stats || {
        activeProjects: 0,
        activeUsers: 0,
        storageUsage: {
          total: 0,
          used: 0
        },
        activityByDay: []
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
      setAlert({
        open: true,
        message: 'Gagal memuat statistik',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    if (tabValue === 0) {
      fetchLogs();
    } else {
      fetchStats();
    }
  }, [tabValue, fetchLogs, fetchStats]); // Sekarang fetchLogs dan fetchStats stabil antar render

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = () => {
    setPage(0);
    fetchLogs();
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: null,
      endDate: null,
      userId: '',
      action: '',
      entityType: '',
      entityId: '',
      projectId: ''
    });
    setPage(0);
    fetchLogs();
  };

  const handleExportLogs = async () => {
    try {
      const params = {};
      
      // Add filters if they exist
      if (filters.startDate && filters.endDate) {
        params.startDate = filters.startDate.toISOString();
        params.endDate = filters.endDate.toISOString();
      }
      if (filters.userId) {
        params.userId = filters.userId;
      }
      if (filters.action) {
        params.action = filters.action;
      }
      if (filters.entityType) {
        params.entityType = filters.entityType;
      }
      if (filters.entityId) {
        params.entityId = filters.entityId;
      }
      if (filters.projectId) {
        params.projectId = filters.projectId;
      }
      
      const response = await api.get('/admin/logs/export', { 
        params,
        responseType: 'blob'
      });
      
      // Create a download link and click it
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setAlert({
        open: true,
        message: 'Log aktivitas berhasil diekspor',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error exporting logs:', error);
      setAlert({
        open: true,
        message: 'Gagal mengekspor log aktivitas',
        severity: 'error'
      });
    }
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  const handleOpenDetailDialog = (log) => {
    setDetailDialog({
      open: true,
      details: log.details,
      log: log
    });
  };

  const handleCloseDetailDialog = () => {
    setDetailDialog({
      open: false,
      details: null,
      log: null
    });
  };

  const getActionChipColor = (action) => {
    switch (action) {
      case 'LOGIN':
      case 'LOGOUT':
        return 'primary';
      case 'CREATE':
        return 'success';
      case 'UPDATE':
        return 'info';
      case 'DELETE':
        return 'error';
      case 'UPLOAD':
      case 'DOWNLOAD':
        return 'secondary';
      case 'APPROVE':
        return 'success';
      case 'REJECT':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  const renderLogs = () => (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6">Filter Log Aktivitas</Typography>
        </Box>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Tanggal Mulai"
                value={filters.startDate}
                onChange={(newValue) => handleFilterChange('startDate', newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Tanggal Akhir"
                value={filters.endDate}
                onChange={(newValue) => handleFilterChange('endDate', newValue)}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="User ID"
              value={filters.userId}
              onChange={(e) => handleFilterChange('userId', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Aksi</InputLabel>
              <Select
                value={filters.action}
                label="Aksi"
                onChange={(e) => handleFilterChange('action', e.target.value)}
              >
                {logActions.map(action => (
                  <MenuItem key={action.value} value={action.value}>
                    {action.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Jenis Entitas</InputLabel>
              <Select
                value={filters.entityType}
                label="Jenis Entitas"
                onChange={(e) => handleFilterChange('entityType', e.target.value)}
              >
                {entityTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Entity ID"
              value={filters.entityId}
              onChange={(e) => handleFilterChange('entityId', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Project ID"
              value={filters.projectId}
              onChange={(e) => handleFilterChange('projectId', e.target.value)}
            />
          </Grid>
        </Grid>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            startIcon={<SearchIcon />}
            onClick={handleApplyFilters}
          >
            Terapkan Filter
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={handleResetFilters}
          >
            Reset Filter
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />}
            onClick={handleExportLogs}
          >
            Ekspor CSV
          </Button>
        </Box>
      </Paper>
      
      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>🕒 Waktu</TableCell>
                    <TableCell>👤 User</TableCell>
                    <TableCell>⚙️ Aktivitas</TableCell>
                    <TableCell>📄 Keterangan</TableCell>
                    <TableCell>🔍 Detail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        Tidak ada data log aktivitas
                      </TableCell>
                    </TableRow>
                  ) : (
                    logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          {new Date(log.timestamp).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          {log.user ? `${log.user.username}${log.user.fullName ? ` (${log.user.fullName})` : ''}` : 'System'}
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={log.action} 
                            color={getActionChipColor(log.action)} 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>{log.description}</TableCell>
                        <TableCell>
                          {log.details ? (
                            <Button 
                              size="small" 
                              variant="text" 
                              onClick={() => handleOpenDetailDialog(log)}
                              startIcon={<SearchIcon />}
                            >
                              Detail
                            </Button>
                          ) : '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={totalLogs}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>
    </Box>
  );

  const renderStats = () => (
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PeopleIcon sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6">Pengguna Aktif</Typography>
              </Box>
              <Typography variant="h4">{stats.activeUsers}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FolderIcon sx={{ mr: 2, color: 'secondary.main' }} />
                <Typography variant="h6">Proyek Aktif</Typography>
              </Box>
              <Typography variant="h4">{stats.activeProjects}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StorageIcon sx={{ mr: 2, color: 'info.main' }} />
                <Typography variant="h6">Penggunaan Penyimpanan</Typography>
              </Box>
              <Typography variant="h4">
                {formatBytes(stats.storageUsage.used)} / {formatBytes(stats.storageUsage.total)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stats.storageUsage.total > 0 
                  ? `${Math.round((stats.storageUsage.used / stats.storageUsage.total) * 100)}% digunakan`
                  : '0% digunakan'
                }
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BarChartIcon sx={{ mr: 2, color: 'success.main' }} />
                <Typography variant="h6">Aktivitas per Hari</Typography>
              </Box>
              {/* Chart would go here in a real implementation */}
              <Typography variant="body1" sx={{ textAlign: 'center', py: 5 }}>
                Grafik aktivitas per hari akan ditampilkan di sini
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>Audit Log & Statistik</Typography>
      
      <Tabs 
        value={tabValue} 
        onChange={handleTabChange} 
        sx={{ mb: 3 }}
      >
        <Tab label="Log Aktivitas" />
        <Tab label="Statistik" />
      </Tabs>
      
      {tabValue === 0 && renderLogs()}
      {tabValue === 1 && renderStats()}
      
      <Snackbar 
        open={alert.open} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
      >
        <Alert 
          onClose={handleCloseAlert} 
          severity={alert.severity} 
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>

      {/* Dialog untuk menampilkan detail log aktivitas */}
      <Dialog
        open={detailDialog.open}
        onClose={handleCloseDetailDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Detail Aktivitas
          <IconButton
            aria-label="close"
            onClick={handleCloseDetailDialog}
            sx={{ color: 'grey.500' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {detailDialog.log && (
            <Grid container spacing={3}>
              {/* Informasi Utama */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, mb: 2, backgroundColor: 'background.paper' }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">🕒 Waktu</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {new Date(detailDialog.log.timestamp).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">👤 User</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {detailDialog.log.user ? `${detailDialog.log.user.username}${detailDialog.log.user.fullName ? ` (${detailDialog.log.user.fullName})` : ''}` : 'System'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" color="text.secondary">⚙️ Aktivitas</Typography>
                      <Chip 
                        label={detailDialog.log.action} 
                        color={getActionChipColor(detailDialog.log.action)} 
                        size="small" 
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">📄 Keterangan</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {detailDialog.log.description}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              
              {/* Informasi Perubahan (jika ada) */}
              {detailDialog.log.details && detailDialog.log.details.changes && (
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mb: 1 }}>Perubahan Data</Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Field</TableCell>
                          <TableCell>Nilai Sebelumnya</TableCell>
                          <TableCell>Nilai Baru</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(detailDialog.log.details.changes).map(([field, change]) => (
                          <TableRow key={field}>
                            <TableCell>{field}</TableCell>
                            <TableCell>{JSON.stringify(change.from)}</TableCell>
                            <TableCell>{JSON.stringify(change.to)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              )}
              
              {/* Informasi Detail Lainnya */}
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Informasi Teknis Tambahan</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Jenis Entitas</Typography>
                        <Typography variant="body2">{detailDialog.log.entityType || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">Entity ID</Typography>
                        <Typography variant="body2">{detailDialog.log.entityId || '-'}</Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" color="text.secondary">IP Address</Typography>
                        <Typography variant="body2">{detailDialog.log.ipAddress || '-'}</Typography>
                      </Grid>
                      {detailDialog.log.details && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary">Detail JSON</Typography>
                          <Paper sx={{ p: 2, backgroundColor: 'background.default', maxHeight: '200px', overflow: 'auto' }}>
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.75rem' }}>
                              {JSON.stringify(detailDialog.log.details, null, 2)}
                            </pre>
                          </Paper>
                        </Grid>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailDialog}>Tutup</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLog;
